import type { Device, DeviceSyncState, SubscriptionState } from './domain'

export type StatusTone = 'success' | 'notice' | 'error' | 'neutral'
export interface StatusPresentation {
  code: string
  label: string
  tone: StatusTone
}

const SYNC_PRESENTATION: Partial<Record<DeviceSyncState, StatusPresentation>> = {
  PENDING: { code: 'pending_sync', label: 'Применяется на серверах', tone: 'notice' },
  ROTATING: { code: 'rotating', label: 'Обновляется на серверах', tone: 'notice' },
  DISABLING: { code: 'disabling', label: 'Отключается', tone: 'notice' },
  DISABLED: { code: 'disabled', label: 'Отключено', tone: 'neutral' },
  SYNC_ERROR: { code: 'sync_error', label: 'Ошибка синхронизации', tone: 'error' },
}

const DEVICE_PRESENTATION: Record<string, StatusPresentation> = {
  active: { code: 'active', label: 'Ключ активен', tone: 'success' },
  pending: { code: 'pending_sync', label: 'Применяется на серверах', tone: 'notice' },
  provisioning: { code: 'provisioning', label: 'Создаётся', tone: 'notice' },
  rotating: { code: 'rotating', label: 'Обновляется на серверах', tone: 'notice' },
  disabling: { code: 'disabling', label: 'Отключается', tone: 'notice' },
  sync_error: { code: 'sync_error', label: 'Ошибка синхронизации', tone: 'error' },
  expired: { code: 'expired', label: 'Срок истёк', tone: 'error' },
  disabled: { code: 'disabled', label: 'Отключено', tone: 'neutral' },
}

export function deviceStatus(device: Pick<Device, 'status' | 'sync_state'>): StatusPresentation {
  if (device.sync_state && device.sync_state !== 'ACTIVE') {
    const sync = SYNC_PRESENTATION[device.sync_state]
    if (sync) return sync
  }
  return DEVICE_PRESENTATION[device.status] || {
    code: 'unknown',
    label: 'Статус уточняется',
    tone: 'neutral',
  }
}

export function subscriptionStatus(state: SubscriptionState): StatusPresentation {
  if (state === 'ACTIVE') return { code: 'active', label: 'Подписка активна', tone: 'success' }
  if (state === 'EXPIRING') return { code: 'expiring', label: 'Скоро продление', tone: 'notice' }
  if (state === 'EXPIRED') return { code: 'expired', label: 'Доступ закончился', tone: 'error' }
  return { code: 'not_started', label: 'Нужна подписка', tone: 'neutral' }
}

export function isSubscriptionActive(state: SubscriptionState): boolean {
  return state === 'ACTIVE' || state === 'EXPIRING'
}

export function remainingDaysLabel(days: number | null | undefined): string {
  if (days == null) return 'без ограничений'
  const value = Math.max(0, Math.trunc(days))
  const mod10 = value % 10
  const mod100 = value % 100
  const noun = mod10 === 1 && mod100 !== 11
    ? 'день'
    : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
      ? 'дня'
      : 'дней'
  return `${value} ${noun}`
}

export type StatusRenderer<Output> = (model: StatusPresentation) => Output

export function createDeviceStatusComponent<Output>(render: StatusRenderer<Output>) {
  return (device: Pick<Device, 'status' | 'sync_state'>): Output => render(deviceStatus(device))
}

export function createSubscriptionStatusComponent<Output>(render: StatusRenderer<Output>) {
  return (state: SubscriptionState): Output => render(subscriptionStatus(state))
}
